<template>
  <div class="author-detail-container">
    <Header />

    <main class="author-detail-main">
      <!-- Breadcrumb Bar -->
      <nav aria-label="breadcrumb" class="breadcrumb-section">
        <div class="container px-3 px-md-4">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link to="/">Trang chủ</router-link>
            </li>
            <li class="breadcrumb-item active" aria-current="page">
              {{ author ? author.name : 'Tác giả' }}
            </li>
          </ol>
        </div>
      </nav>

      <!-- Editorial Author Hero Banner -->
      <section class="author-hero-section">
        <div class="container px-3 px-md-4">
          <div class="author-hero-card">
            <div class="author-hero-main">
              <div class="author-label-tag">Tác giả</div>
              <h1 class="author-title">
                <span v-if="author">{{ author.name }}</span>
                <span v-else-if="isLoading" class="skeleton-shimmer d-inline-block" style="width: 220px; height: 32px; border-radius: 6px;"></span>
                <span v-else>Tác giả</span>
                <span v-if="author && (birthYear || deathYear)" class="author-year-badge">
                  {{ authorLifeSpanText }}
                </span>
              </h1>

              <!-- Author Categories / Genres Row -->
              <div class="author-genres-row" v-if="authorCategories && authorCategories.length > 0">
                <span class="genres-prefix">Chủ đề sáng tác:</span>
                <div class="genres-chips-wrap">
                  <router-link
                    v-for="cat in previewCategories"
                    :key="cat.id"
                    :to="'/category/' + cat.id"
                    class="genre-chip"
                  >
                    {{ cat.name }}
                  </router-link>
                  <button
                    v-if="hasMoreCategories"
                    type="button"
                    class="genre-chip chip-more"
                    @click="openCategoriesModal"
                    title="Xem toàn bộ chủ đề tác phẩm"
                  >
                    +{{ extraCategoriesCount }} khác
                  </button>
                </div>
              </div>
            </div>

            <!-- Right Stat Column: Clean Editorial Metric Card -->
            <div class="author-hero-aside">
              <div class="author-metric-card">
                <div class="metric-card-header">
                  <span class="metric-card-title">Dữ liệu tại Lối Đọc</span>
                </div>
                <div class="metric-card-body">
                  <div class="metric-item">
                    <span class="metric-value">{{ totalElements }}</span>
                    <span class="metric-label">Tác phẩm</span>
                  </div>
                  <div class="metric-divider" v-if="authorCategories.length > 0"></div>
                  <div class="metric-item" v-if="authorCategories.length > 0">
                    <span class="metric-value">{{ authorCategories.length }}</span>
                    <span class="metric-label">Chủ đề</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Author Catalog Section -->
      <section class="author-catalog-section">
        <div class="container px-3 px-md-4">
          <!-- Toolbar -->
          <div class="catalog-toolbar">
            <div class="toolbar-left">
              <h2 class="catalog-heading">
                Danh sách tác phẩm
              </h2>
            </div>

            <div class="toolbar-right">
              <!-- Quick search -->
              <div class="search-filter-box">
                <icon icon="fa-solid fa-magnifying-glass" class="search-icon" />
                <input
                  type="text"
                  v-model="searchQuery"
                  @input="onSearchInput"
                  class="search-filter-input"
                  placeholder="Tìm kiếm tác phẩm của tác giả..."
                />
                <button
                  v-if="searchQuery"
                  class="clear-filter-btn"
                  @click="clearSearch"
                  title="Xóa tìm kiếm"
                >
                  <icon icon="fa-solid fa-xmark" />
                </button>
              </div>

              <!-- Sort -->
              <div class="sort-select-wrapper">
                <select v-model="sortBy" @change="onSortChange" class="sort-select">
                  <option value="default">Sắp xếp: Mặc định</option>
                  <option value="rating-desc">Đánh giá cao nhất</option>
                  <option value="name-asc">Tên sách (A - Z)</option>
                  <option value="name-desc">Tên sách (Z - A)</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Loading State (Skeleton Grid) -->
          <div v-if="isLoading" class="row row-cols-2 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-3 g-md-4">
            <div v-for="n in 10" :key="'skel-' + n" class="col">
              <div class="skeleton-book-card">
                <div class="skeleton-shimmer skeleton-cover"></div>
                <div class="skeleton-shimmer skeleton-line skeleton-title"></div>
                <div class="skeleton-shimmer skeleton-line skeleton-author"></div>
              </div>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="errorMessage" class="state-empty-card">
            <div class="state-icon-circle text-danger">
              <icon icon="fa-solid fa-circle-exclamation" />
            </div>
            <h3 class="state-title">Đã xảy ra lỗi tải dữ liệu</h3>
            <p class="state-desc">{{ errorMessage }}</p>
            <button @click="retry" class="btn btn-outline-dark px-4 rounded-pill">
              <icon icon="fa-solid fa-rotate-right" class="me-2" />Thử lại
            </button>
          </div>

          <!-- Empty State (No search results when searching) -->
          <div v-else-if="books.length === 0 && searchQuery" class="state-empty-card">
            <div class="state-icon-circle">
              <icon icon="fa-solid fa-filter-circle-xmark" />
            </div>
            <h3 class="state-title">Không tìm thấy tác phẩm</h3>
            <p class="state-desc">Không có cuốn sách nào của tác giả khớp với từ khóa "<strong>{{ searchQuery }}</strong>".</p>
            <button @click="clearSearch" class="btn btn-outline-dark px-4 rounded-pill">
              <icon icon="fa-solid fa-rotate-left" class="me-2" />Xóa tìm kiếm
            </button>
          </div>

          <!-- Empty State (No books at all) -->
          <div v-else-if="books.length === 0" class="state-empty-card">
            <div class="state-icon-circle">
              <icon icon="fa-solid fa-book-open-reader" />
            </div>
            <h3 class="state-title">Chưa có tác phẩm nào</h3>
            <p class="state-desc">Tác giả này hiện chưa có ấn phẩm nào được cập nhật trên hệ thống. Vui lòng quay lại sau!</p>
            <router-link to="/" class="btn btn-dark px-4 rounded-pill">
              <icon icon="fa-solid fa-arrow-left" class="me-2" />Về trang chủ
            </router-link>
          </div>

          <!-- Book Grid Catalog -->
          <div v-else>
            <div class="row row-cols-2 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-3 g-md-4">
              <div v-for="book in books" :key="book.id" class="col">
                <BookCard
                  :item="book"
                  mode="catalog"
                  @click="$router.push('/book/' + (book.id || book.bookId))"
                />
              </div>
            </div>

            <!-- Pagination Controls -->
            <nav v-if="totalPages > 1" class="d-flex justify-content-center mt-5" aria-label="Author books navigation">
              <ul class="custom-pagination d-flex align-items-center gap-1 list-unstyled p-1.5 rounded-pill shadow-sm">
                <!-- Previous Button -->
                <li class="page-item-custom">
                  <button
                    @click="goToPage(currentPage - 1)"
                    :disabled="currentPage === 0"
                    class="page-link-custom prev-next-btn d-flex align-items-center justify-content-center"
                    aria-label="Trang trước"
                  >
                    <icon icon="fa-solid fa-chevron-left" />
                  </button>
                </li>

                <!-- Numeric page links -->
                <li
                  v-for="page in visiblePages"
                  :key="page"
                  class="page-item-custom"
                >
                  <!-- Ellipsis placeholder -->
                  <span v-if="page === '...'" class="page-link-ellipsis">...</span>
                  <!-- Page link button -->
                  <button
                    v-else
                    @click="goToPage(page)"
                    :class="['page-link-custom numeric-btn d-flex align-items-center justify-content-center', { 'active': currentPage === page }]"
                  >
                    {{ page + 1 }}
                  </button>
                </li>

                <!-- Next Button -->
                <li class="page-item-custom">
                  <button
                    @click="goToPage(currentPage + 1)"
                    :disabled="currentPage === totalPages - 1"
                    class="page-link-custom prev-next-btn d-flex align-items-center justify-content-center"
                    aria-label="Trang sau"
                  >
                    <icon icon="fa-solid fa-chevron-right" />
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </section>
    </main>

    <!-- Modal: Tất cả chủ đề tác phẩm của tác giả -->
    <transition name="modal-fade">
      <div
        v-if="showAllCategoriesModal"
        class="modal-backdrop-custom"
        @click.self="closeCategoriesModal"
      >
        <div class="modal-dialog-custom" role="dialog" aria-modal="true">
          <div class="modal-content-card">
            <!-- Modal Header -->
            <div class="modal-header-custom">
              <div class="d-flex align-items-center gap-3">
                <div class="modal-icon-badge">
                  <icon icon="fa-solid fa-layer-group" />
                </div>
                <div>
                  <h3 class="modal-title-custom">Chủ đề & Thể loại tác phẩm</h3>
                  <p class="modal-subtitle-custom">
                    Tổng hợp tất cả chủ đề có tác phẩm phát hành của tác giả <strong>{{ author ? author.name : '' }}</strong>
                  </p>
                </div>
              </div>
              <button
                type="button"
                class="btn-close-custom"
                @click="closeCategoriesModal"
                aria-label="Đóng"
              >
                <icon icon="fa-solid fa-xmark" />
              </button>
            </div>

            <!-- Modal Body (Grid of Categories) -->
            <div class="modal-body-custom">
              <div class="modal-categories-grid">
                <router-link
                  v-for="cat in authorCategories"
                  :key="cat.id"
                  :to="'/category/' + cat.id"
                  class="modal-cat-card"
                  @click="closeCategoriesModal"
                >
                  <div class="modal-cat-icon">
                    <icon icon="fa-solid fa-bookmark" />
                  </div>
                  <div class="modal-cat-details">
                    <span class="modal-cat-name">{{ cat.name }}</span>
                    <span class="modal-cat-link">Xem danh mục &rarr;</span>
                  </div>
                </router-link>
              </div>
            </div>

            <!-- Modal Footer -->
            <div class="modal-footer-custom">
              <button
                type="button"
                class="btn btn-dark px-4 rounded-pill"
                @click="closeCategoriesModal"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <Footer />
  </div>
</template>

<script src="./author-detail.js"></script>
<style scoped src="./author-detail.css"></style>
